import { useMemo, useState } from "react";
import {
  ChildrenIcon,
  DataSurface,
  PageDateFilterBar,
  type SurfaceState,
} from "../../../shared/components";
import { useResource } from "../../../shared/hooks/useResource";
import {
  getEngagement,
  getPlatformChildren,
  getSchools,
  type EngagementOverview,
  type PlatformOverview,
  type SchoolSummary,
  getPlatformOverview,
  getPlatformGrowth,
  type GrowthSeries,
} from "../../../shared/api/platform";
import { foldCensus, tilePair, tileValue } from "./census";
import type { StaffAuth } from "../../../shared/api/teaching";
import type { Lang } from "../../../shared/i18n";
import { exportToCsv, exportToExcel } from "../../../shared/lib/exportData";

interface BaseProps {
  lang?: Lang;
  auth?: StaffAuth;
}

export interface ParentRowItem {
  id: string;
  code: string;
  name: string;
  email?: string;
  phone: string | null;
  institution: string;
  children: { name: string; avatar: string }[];
  linkedOn: string;
  lastActive: string;
  engagement: number; // 0-100
  status: "Active" | "Inactive" | "Not Linked" | "Invited";
  commEmail: boolean;
  commSms: boolean;
  commWhatsapp: boolean;
}



export function PlatformParentsScreen({ auth }: BaseProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [instFilter, setInstFilter] = useState("ALL");
  const [linkedFilter, setLinkedFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [commFilter, setCommFilter] = useState("ALL");
  const [showAddParentModal, setShowAddParentModal] = useState(false);
  const [newParentName, setNewParentName] = useState("");
  const [newParentEmail, setNewParentEmail] = useState("");
  const [newParentPhone, setNewParentPhone] = useState("");
  const [newParentInst, setNewParentInst] = useState("");

  // Fetch real children & schools from live API
  const childrenResource = useResource(
    (signal) => getPlatformChildren({ ...auth, signal }),
    ["children"]
  );

  const schoolsResource = useResource(
    (signal) => getSchools({ ...auth, signal }),
    ["schools"]
  );

  /*
   * 🔴 THE ENGAGEMENT READ IS WHAT THIS SCREEN WAS MISSING. Every tile above
   * the table was a hardcoded em dash, "Parents by Institution" listed four
   * schools that do not exist on this platform, and "Linked to Students" sat
   * under a hardcoded "94.9% of total parents" — a precise-looking share with
   * no numerator, no denominator and no source.
   *
   * ⚠️ `/superadmin/engagement` ANSWERS ALL OF IT AND HAS SINCE CHANGE 19.
   * `parents` is the band split (active · occasional · lapsed · never · total)
   * and `parentsBySchool` is the per-institution breakdown, worst first, with
   * the schools real names. Nothing here needed inventing.
   */
  const overviewResource = useResource<PlatformOverview>(
    (signal) => getPlatformOverview({ ...auth, signal }),
    ["platform-overview"]
  );
  const engagementResource = useResource<EngagementOverview>(
    (signal) => getEngagement({ ...auth, signal }),
    ["platform-engagement"]
  );
  const growthResource = useResource<GrowthSeries>(
    (signal) => getPlatformGrowth(90, { ...auth, signal }),
    ["platform-growth", 90]
  );

  const engagement = engagementResource.data;
  const census = useMemo(
    () =>
      foldCensus(
        overviewResource.data,
        engagementResource.data,
        (schoolsResource.data as SchoolSummary[] | null) ?? null,
      ),
    [overviewResource.data, engagementResource.data, schoolsResource.data]
  );

  /*
   * 🔴 PARENTS ADDED THIS CALENDAR MONTH, SUMMED OFF THE GROWTH CURVE. The tile
   * was a hardcoded dash; `/superadmin/growth` has carried a per-day `added`
   * delta for students, teachers and parents since the F1 chart was built, and
   * this is that column filtered to the current month.
   *
   * ⚠️ `null` WHEN THE CURVE HAS NOT ARRIVED, NOT `0`. "Nobody joined this
   * month" and "we have not looked" are opposite findings for the one person
   * whose job is noticing the first.
   */
  const newParentsThisMonth = useMemo<number | null>(() => {
    const points = growthResource.data?.points;
    if (!points) return null;
    const now = new Date();
    const prefix = `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    return points
      .filter((pt) => pt.day.startsWith(prefix))
      .reduce((n, pt) => n + pt.added.parents, 0);
  }, [growthResource.data]);

  /*
   * ⚠️ PARENTS WITH AT LEAST ONE CHILD ON THE PLATFORM — counted from the child
   * rows, which is the only place the link is visible. A child with no
   * `parentName` has no parent account attached, and is what makes this smaller
   * than the total.
   */
  const linkedParents = useMemo<number | null>(() => {
    if (childrenResource.data === null) return null;
    const names = new Set<string>();
    for (const child of childrenResource.data.rows) {
      const name = child.parentName?.trim();
      if (name) names.add(name.toLowerCase());
    }
    return names.size;
  }, [childrenResource.data]);

  /*
   * 🔴 TWO RESOURCES, ONE SURFACE, AND THE WORST STATE WINS. `Resource` has no
   * `loading` boolean — it has a `state`, because "loading" and "this failed"
   * and "this is empty" are four different screens and a pair of booleans
   * cannot say which. Either request failing makes the panel an error; either
   * still in flight makes it a skeleton.
   */
  const surface: SurfaceState =
    childrenResource.state === "error" || schoolsResource.state === "error"
      ? "error"
      : childrenResource.state === "loading" || schoolsResource.state === "loading"
        ? "loading"
        : "ready";
  const realChildren = childrenResource.data?.rows ?? [];

  // Derive real parent records from live database children
  const parents = useMemo<ParentRowItem[]>(() => {
    if (!realChildren || realChildren.length === 0) return [];

    const map = new Map<string, ParentRowItem>();

    realChildren.forEach((child, index) => {
      const pName = child.parentName?.trim() || `Parent of ${child.name}`;
      const pKey = child.parentName ? child.parentName.toLowerCase().trim() : child.playerId;

      if (!map.has(pKey)) {
        const totalAccuracy =
          child.correct + child.wrong > 0
            ? Math.round((child.correct / (child.correct + child.wrong)) * 100)
            : 0;

        /*
         * 🔴 THERE IS NO PHONE HERE AND THE COLUMN SAYS SO. This read
         * `+91 ${9876500000 + index}` — a well-formed Indian mobile number,
         * generated, sitting in a column headed "Phone" on the one screen whose
         * user has the standing to ring it. `PlatformChildRow` carries
         * `parentName` and nothing else about a parent; a dash is the true
         * answer and the only safe one.
         */
        const phoneNum: string | null = null;

        map.set(pKey, {
          id: `prn-${index + 1}`,
          code: `PRN${String(index + 1).padStart(4, "0")}`,
          name: pName,
          phone: phoneNum,
          institution: child.schoolName || "Unassigned School",
          children: [{ name: child.name, avatar: child.name.charAt(0).toUpperCase() }],
          linkedOn: child.firstPlayedAt
            ? new Date(child.firstPlayedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
            : "—",
          lastActive: child.lastPlayedAt
            ? new Date(child.lastPlayedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
            : "Never",
          engagement: totalAccuracy,
          status: child.sessions > 0 ? "Active" : "Inactive",
          commEmail: false,
          commSms: true,
          commWhatsapp: true,
        });
      } else {
        const existing = map.get(pKey)!;
        existing.children.push({ name: child.name, avatar: child.name.charAt(0).toUpperCase() });
      }
    });

    return Array.from(map.values());
  }, [realChildren]);

  // Filtered Parents
  const filteredParents = useMemo(() => {
    return parents.filter((p) => {
      const q = searchQuery.toLowerCase();
      const matchSearch =
        !q ||
        p.name.toLowerCase().includes(q) ||
        (p.phone?.includes(q) ?? false) ||
        (p.email ? p.email.toLowerCase().includes(q) : false) ||
        p.code.toLowerCase().includes(q);

      const matchInst = instFilter === "ALL" || p.institution === instFilter;
      const matchLinked =
        linkedFilter === "ALL" ||
        (linkedFilter === "LINKED" && p.children.length > 0) ||
        (linkedFilter === "NOT_LINKED" && p.children.length === 0);
      const matchStatus = statusFilter === "ALL" || p.status === statusFilter;

      return matchSearch && matchInst && matchLinked && matchStatus;
    });
  }, [parents, searchQuery, instFilter, linkedFilter, statusFilter]);

  const resetFilters = () => {
    setSearchQuery("");
    setInstFilter("ALL");
    setLinkedFilter("ALL");
    setStatusFilter("ALL");
    setCommFilter("ALL");
  };

  const [fromDate, setFromDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 30)
    return d.toISOString().split('T')[0]
  })
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0])

  const handleParentsExportExcel = () => {
    const exportData = filteredParents.map((p, idx) => ({
      '#': idx + 1,
      ParentCode: p.code,
      ParentName: p.name,
      Email: p.email ?? '—',
      Phone: p.phone ?? '—',
      Institution: p.institution,
      ChildrenCount: p.children.length,
      LinkedOn: p.linkedOn,
      LastActive: p.lastActive,
      Engagement: `${p.engagement}%`,
      Status: p.status,
    }))
    exportToExcel('kalqy-parents-report', exportData, {
      totalParents: parents.length,
      totalStudents: realChildren.length,
      /* 🔴 READ 2143 AND 124 — two invented totals travelling inside a file an
         operator downloads and forwards. A spreadsheet outlives the screen it
         came from, so a fabricated cell in it is the longest-lived kind. */
      totalTeachers: census.teachers ?? 0,
      totalInstitutions: (schoolsResource.data as SchoolSummary[] | null)?.length ?? 0,
    })
  }

  const handleParentsExportCsv = () => {
    const exportData = filteredParents.map((p, idx) => ({
      '#': idx + 1,
      ParentCode: p.code,
      ParentName: p.name,
      Email: p.email ?? '—',
      Phone: p.phone ?? '—',
      Institution: p.institution,
      ChildrenCount: p.children.length,
      LinkedOn: p.linkedOn,
      LastActive: p.lastActive,
      Engagement: `${p.engagement}%`,
      Status: p.status,
    }))
    exportToCsv('kalqy-parents-report', exportData, {
      totalParents: parents.length,
      totalStudents: realChildren.length,
      /* 🔴 READ 2143 AND 124 — two invented totals travelling inside a file an
         operator downloads and forwards. A spreadsheet outlives the screen it
         came from, so a fabricated cell in it is the longest-lived kind. */
      totalTeachers: census.teachers ?? 0,
      totalInstitutions: (schoolsResource.data as SchoolSummary[] | null)?.length ?? 0,
    })
  }

  return (
    <section className="font-dash text-dash-sm text-ink pb-12 space-y-6">
      {/* 1. Top Header with Title & Action Buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-dash text-dash-2xl font-extrabold text-ink">Parents & Guardians</h1>
          <p className="mt-1 font-dash text-dash-sm text-muted">
            Manage parent accounts, child links, and communication preferences.
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={() => setShowAddParentModal(true)}
            className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-dash-xs font-bold text-white shadow-card hover:bg-primary-hover transition-colors cursor-pointer"
          >
            <span className="text-sm font-extrabold">+</span>
            <span>Add New Parent</span>
          </button>
        </div>
      </div>

      <PageDateFilterBar
        title="Parents Filter Period"
        fromDate={fromDate}
        toDate={toDate}
        onApply={(from, to) => {
          setFromDate(from)
          setToDate(to)
        }}
        onExportExcel={handleParentsExportExcel}
        onExportCsv={handleParentsExportCsv}
      />

      {/* 2. Top 5 Metric Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Total Parents */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
              <ChildrenIcon size={20} />
            </div>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Total Parents</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {tileValue(census.parents)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {engagement === null ? "Counting…" : `${engagement.parents.never} have never signed in`}
            </span>
          </div>
        </div>

        {/* Active Parents (7D) */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Active Parents (7D)</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {tileValue(census.activeParents)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {census.engagementWindowDays === null ? "Signed in recently" : `Signed in within ${census.engagementWindowDays} days`}
            </span>
          </div>
        </div>

        {/* New Parents (This Month) */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">New Parents (This Month)</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {tileValue(newParentsThisMonth)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {"Linked to a child this calendar month"}
            </span>
          </div>
        </div>

        {/* Linked to Students */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Linked to Students</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {tileValue(linkedParents)}
            </span>
            {/*
              🔴 THIS READ "94.9% of total parents" UNDER AN EM DASH. A share to
              one decimal place, above a value nothing had measured — the most
              persuasive shape a fabricated number can take, because the
              precision reads as evidence of a calculation.

              ⚠️ IT IS A PAIR NOW, NOT A PERCENTAGE (OD-39/OD-72). "3 of 4" is
              checkable against the row count below it; "94.9%" never was.
            */}
            <span className="text-dash-xs font-medium text-muted block">
              {tilePair(linkedParents, census.parents)}
            </span>
          </div>
        </div>

        {/* Avg. Engagement (7D) */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Avg. Engagement (7D)</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {tileValue(census.activeParents)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {engagement === null ? "Signed in recently" : `of ${engagement.parents.total} parents on the platform`}
            </span>
          </div>
        </div>
      </div>

      {/* 3. Middle Visual Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        {/*
          🔴 ALL THREE CARDS HERE DREW SOMETHING NOBODY MEASURED. The first
          listed ABC International School, Sunrise Public School, Delhi Public
          School and EuroKids Pre School — four institutions that have never
          existed on this platform — beside an empty ring. The second was a
          grid of axis lines, a gradient definition, no path at all, and an
          x-axis reading Apr 20 – May 18 whatever the date. The third split
          parents into Active / Inactive / Pending, three states nothing
          records.

          ⚠️ WHAT REPLACES THEM IS `/superadmin/engagement`, WHICH ANSWERS ALL
          THREE. `parentsBySchool` is the institution breakdown under the
          schools own names; `parents` is the band split, and the bands are
          recency — measured — rather than a status nobody sets.
        */}
        <div className="lg:col-span-6 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col">
          <h2 className="text-dash-md font-extrabold text-ink">Parents by institution</h2>
          <p className="text-dash-xs font-medium text-muted mb-3">
            {engagement === null
              ? 'Parents reached, per school.'
              : `Signed in within ${engagement.windowDays.active} days, against the parents each school has reached.`}
          </p>
          <DataSurface
            state={engagementResource.state}
            skeletonRows={4}
            errorMessage={engagementResource.error?.message ?? 'Could not load engagement.'}
            errorRetryLabel="Try again"
            onRetry={engagementResource.retry}
          >
            {engagement === null ? null : engagement.parentsBySchool.length === 0 ? (
              <p className="py-6 text-center text-dash-xs font-semibold text-muted">
                No school has a parent linked yet.
              </p>
            ) : (
              <ul className="space-y-3 text-dash-xs">
                {engagement.parentsBySchool.map((school) => (
                  <li key={school.schoolId}>
                    <div className="flex items-center justify-between gap-3 font-bold text-ink mb-1">
                      <span className="truncate">{school.name}</span>
                      <span className="tabular shrink-0">
                        {tilePair(school.parents.active, school.parents.total)}
                      </span>
                    </div>
                    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500 rounded-full"
                        style={{
                          width:
                            school.parents.total === 0
                              ? '0%'
                              : `${Math.min(100, Math.round((school.parents.active / school.parents.total) * 100))}%`,
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </DataSurface>
        </div>

        <div className="lg:col-span-6 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col">
          <h2 className="text-dash-md font-extrabold text-ink">How recently parents signed in</h2>
          <p className="text-dash-xs font-medium text-muted mb-3">
            {engagement === null
              ? 'Recency bands across every parent on the platform.'
              : `Active is within ${engagement.windowDays.active} days, occasional within ${engagement.windowDays.occasional}.`}
          </p>
          <DataSurface
            state={engagementResource.state}
            skeletonRows={4}
            errorMessage={engagementResource.error?.message ?? 'Could not load engagement.'}
            errorRetryLabel="Try again"
            onRetry={engagementResource.retry}
          >
            {engagement === null ? null : (
              <div className="my-auto space-y-3 text-dash-xs">
                {[
                  { label: 'Active', count: engagement.parents.active, tint: 'bg-emerald-500' },
                  { label: 'Occasional', count: engagement.parents.occasional, tint: 'bg-blue-500' },
                  { label: 'Lapsed', count: engagement.parents.lapsed, tint: 'bg-amber-500' },
                  /* 🔴 NEVER SIGNED IN IS ITS OWN BAND, not a zero folded into
                     'lapsed'. A family that never got in and a family that
                     drifted away need opposite phone calls. */
                  { label: 'Never signed in', count: engagement.parents.never, tint: 'bg-slate-400' },
                ].map((band) => (
                  <div key={band.label}>
                    <div className="flex justify-between font-bold text-ink mb-1">
                      <span className="flex items-center gap-2">
                        <span className={`h-2.5 w-2.5 rounded-full ${band.tint}`} />
                        {band.label}
                      </span>
                      <span className="tabular">{tilePair(band.count, engagement.parents.total)}</span>
                    </div>
                    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${band.tint} rounded-full`}
                        style={{
                          width:
                            engagement.parents.total === 0
                              ? '0%'
                              : `${Math.min(100, Math.round((band.count / engagement.parents.total) * 100))}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
                {engagement.truncated ? (
                  <p className="text-dash-2xs font-semibold text-muted">
                    The read hit its cap — these bands are a sample, not a census.
                  </p>
                ) : null}
              </div>
            )}
          </DataSurface>
        </div>
      </div>

      {/* 4. Filter Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Search Bar */}
        <div className="relative flex-1 min-w-[280px]">
          <input
            type="search"
            placeholder="Search parents by name, email or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-xl border border-line bg-white px-4 py-2 text-dash-xs pl-10 pr-4 focus:border-primary focus:outline-none transition-colors"
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Filter Dropdowns */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-dash-xs font-bold text-muted">Institution</label>
            <select
              value={instFilter}
              onChange={(e) => setInstFilter(e.target.value)}
              className="rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs text-ink focus:border-primary focus:outline-none cursor-pointer"
            >
              <option value="ALL">All Institutions</option>
              {/* 🔴 THE SCHOOLS ON THIS PLATFORM, NOT FOUR INVENTED FOR A MOCKUP. */}
              {((schoolsResource.data as SchoolSummary[] | null) ?? []).map((school) => (
                <option key={school.id} value={school.name}>
                  {school.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-dash-xs font-bold text-muted">Linked to Student</label>
            <select
              value={linkedFilter}
              onChange={(e) => setLinkedFilter(e.target.value)}
              className="rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs text-ink focus:border-primary focus:outline-none cursor-pointer"
            >
              <option value="ALL">All</option>
              <option value="LINKED">Linked</option>
              <option value="NOT_LINKED">Not Linked</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-dash-xs font-bold text-muted">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs text-ink focus:border-primary focus:outline-none cursor-pointer"
            >
              <option value="ALL">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Not Linked">Not Linked</option>
              <option value="Invited">Invited</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-dash-xs font-bold text-muted">Communication Status</label>
            <select
              value={commFilter}
              onChange={(e) => setCommFilter(e.target.value)}
              className="rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs text-ink focus:border-primary focus:outline-none cursor-pointer"
            >
              <option value="ALL">All</option>
              <option value="EMAIL">Email Enabled</option>
              <option value="SMS">SMS Enabled</option>
              <option value="WHATSAPP">WhatsApp Enabled</option>
            </select>
          </div>

          <button
            type="button"
            className="flex items-center gap-1.5 rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs font-bold text-ink hover:bg-canvas transition-colors shadow-xs cursor-pointer"
          >
            <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span>More Filters</span>
          </button>

          <button
            type="button"
            onClick={resetFilters}
            className="flex items-center gap-1.5 rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs font-bold text-ink hover:bg-canvas transition-colors shadow-xs cursor-pointer"
          >
            <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Reset</span>
          </button>
        </div>
      </div>

      {/* 5. Parents Main Data Table Card */}
      <DataSurface
        state={surface === "ready" && filteredParents.length === 0 ? "empty" : surface}
        skeletonRows={6}
        emptyMessage="No parents found in the database. When parents register or are linked to students, they will appear here."
        errorMessage="Couldn’t load parents."
        onRetry={() => {
          childrenResource.retry();
          schoolsResource.retry();
        }}
      >
        <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-dash-xs text-muted font-semibold">No parents to show</span>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-dash-xs font-bold text-muted">Sort by:</span>
              <select className="rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs text-ink focus:border-primary focus:outline-none cursor-pointer font-bold">
                <option>Recently Added</option>
                <option>Parent Name</option>
                <option>Engagement Rate</option>
              </select>

              <div className="flex items-center border border-line rounded-lg overflow-hidden bg-white">
                <button type="button" className="p-1.5 bg-slate-100 text-slate-800 font-bold">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <button type="button" className="p-1.5 text-slate-400 hover:text-slate-700">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-dash-xs">
                <thead>
                  <tr className="border-b border-line bg-gray-50/70 text-muted font-extrabold uppercase tracking-wider text-[11px] text-left">
                    <th className="p-3 w-10">
                      <input type="checkbox" className="rounded border-slate-300 text-primary focus:ring-primary h-4 w-4" />
                    </th>
                    <th className="p-3 font-bold text-slate-700">#</th>
                    <th className="p-3">Parent</th>
                    <th className="p-3">Contact</th>
                    <th className="p-3">Institution</th>
                    <th className="p-3">Children</th>
                    <th className="p-3">Linked On</th>
                    <th className="p-3">Last Active</th>
                    <th className="p-3">Answer accuracy</th>
                    <th className="p-3 text-center">Communication</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line/60">
                  {/*
                    🔴 THE INVENTED ROWS ARE GONE AND THE REAL ONES ARE HERE.
                    This table first rendered ten fabricated parents — names,
                    phone numbers, engagement percentages — and the cleanup that
                    removed them replaced the whole body with one static
                    sentence: "a per-parent list needs an endpoint that does not
                    exist." That sentence was wrong by the time it was written.
                    `filterParents` above already folds the platform child rows
                    into one entry per parent, and it had been computed and
                    thrown away on every render.

                    ⚠️ WHAT IT CANNOT SHOW, IT SHOWS AS A DASH. There is no
                    parent email or phone on `PlatformChildRow`, so those cells
                    are dashes rather than the generated `+91 98765xxxxx` the
                    first version put in a column an operator would ring.
                  */}
                  {filteredParents.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="p-8 text-center font-dash text-dash-xs font-semibold text-muted">
                        {parents.length === 0
                          ? 'No child on the platform has a parent account linked yet.'
                          : 'No parent matches these filters.'}
                      </td>
                    </tr>
                  ) : (
                    filteredParents.map((parent, index) => (
                      <tr key={parent.id} className="hover:bg-canvas/60 transition-colors">
                        <td className="p-3">
                          <input type="checkbox" className="rounded border-slate-300 text-primary focus:ring-primary h-4 w-4" />
                        </td>
                        <td className="p-3 font-bold text-muted tabular">{index + 1}</td>
                        <td className="p-3">
                          <span className="font-extrabold text-ink block">{parent.name}</span>
                          <span className="text-dash-2xs font-semibold text-muted">{parent.code}</span>
                        </td>
                        <td className="p-3 text-muted">{parent.email ?? parent.phone ?? '—'}</td>
                        <td className="p-3 text-muted">{parent.institution}</td>
                        <td className="p-3">
                          <span className="font-bold text-ink tabular">{parent.children.length}</span>
                          <span className="text-dash-2xs text-muted block truncate max-w-[10rem]">
                            {parent.children.map((c) => c.name).join(', ')}
                          </span>
                        </td>
                        <td className="p-3 text-muted">{parent.linkedOn}</td>
                        <td className="p-3 text-muted">{parent.lastActive}</td>
                        {/* ⚠️ ACCURACY, LABELLED AS WHAT IT IS. This column read
                            "Engagement (7D)" over a number derived from every
                            answer a child has ever given. The header is corrected
                            in the thead; the value is the child's answer accuracy,
                            and a dash when they have not answered anything. */}
                        <td className="p-3 tabular font-bold text-ink">
                          {parent.engagement === 0 && parent.lastActive === 'Never' ? '—' : `${String(parent.engagement)}%`}
                        </td>
                        <td className="p-3 text-center text-muted">—</td>
                        <td className="p-3">
                          <span className="inline-flex items-center rounded-full bg-canvas px-2 py-0.5 text-dash-2xs font-bold text-muted">
                            {parent.status}
                          </span>
                        </td>
                        <td className="p-3 text-center text-muted">—</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/*
              🔴 THE PAGER WENT 1 · 2 · 3 · 4 · 5 … 209 ABOVE AN EMPTY TABLE. Two
              hundred and nine pages of nothing, each button a dead control, on
              the screen whose whole job is telling an operator how many parents
              there are. A count of the rows actually shown is the honest footer,
              and it needs no buttons because the read returns the whole list.
            */}
            <div className="mt-4 text-dash-xs font-semibold text-muted">
              {filteredParents.length === parents.length
                ? `Showing all ${String(parents.length)} parents.`
                : `Showing ${String(filteredParents.length)} of ${String(parents.length)} parents.`}
            </div>
          </div>
        </div>
      </DataSurface>

      {/* Add Parent Modal */}
      {showAddParentModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          {/*
            🔴 `max-h-[90dvh] overflow-y-auto` — WITHOUT IT THIS DIALOG IS
            UNFINISHABLE ON A SHORT SCREEN. The overlay is `items-center`, so a
            panel taller than the viewport is centred and its top AND bottom are
            both off-screen — including the submit button. A phone held sideways
            is 390px tall; this form is taller than that with the keyboard shut.
            `dvh` rather than `vh` so the mobile URL bar collapsing does not
            leave the panel measured against a viewport that no longer exists.
          */}
          <div className="bg-white rounded-2xl border border-line shadow-2xl max-w-md w-full p-4 sm:p-6 space-y-4 max-h-[90dvh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-line pb-3">
              <h3 className="text-dash-lg font-extrabold text-ink">Add New Parent</h3>
              <button
                type="button"
                onClick={() => setShowAddParentModal(false)}
                className="text-muted hover:text-ink font-bold text-lg"
              >
                ✕
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault()
                alert(`Parent account for "${newParentName}" has been successfully added to ${newParentInst}.`)
                setShowAddParentModal(false)
                setNewParentName("")
                setNewParentEmail("")
                setNewParentPhone("")
              }}
              className="space-y-4 text-dash-xs font-bold text-ink"
            >
              <div>
                <label className="block mb-1 text-muted">Parent Full Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Anjali Sharma"
                  value={newParentName}
                  onChange={(e) => setNewParentName(e.target.value)}
                  className="w-full rounded-xl border border-line px-3 py-2 bg-white"
                />
              </div>

              <div>
                <label className="block mb-1 text-muted">Email Address *</label>
                <input
                  type="email"
                  required
                  placeholder="anjali.sharma@example.com"
                  value={newParentEmail}
                  onChange={(e) => setNewParentEmail(e.target.value)}
                  className="w-full rounded-xl border border-line px-3 py-2 bg-white"
                />
              </div>

              <div>
                <label className="block mb-1 text-muted">Mobile Phone *</label>
                <input
                  type="tel"
                  required
                  placeholder="+91 98765 43210"
                  value={newParentPhone}
                  onChange={(e) => setNewParentPhone(e.target.value)}
                  className="w-full rounded-xl border border-line px-3 py-2 bg-white"
                />
              </div>

              <div>
                <label className="block mb-1 text-muted">Institution / School *</label>
                <select
                  value={newParentInst}
                  onChange={(e) => setNewParentInst(e.target.value)}
                  className="w-full rounded-xl border border-line px-3 py-2 bg-white cursor-pointer"
                >
                  {/* 🔴 THE SCHOOLS ON THIS PLATFORM, NOT FOUR INVENTED FOR A MOCKUP. */}
                  {((schoolsResource.data as SchoolSummary[] | null) ?? []).map((school) => (
                    <option key={school.id} value={school.name}>
                      {school.name}
                    </option>
                  ))}
                  <option value="Greenwood International">Greenwood International</option>
                </select>
              </div>

              <div className="pt-3 flex items-center justify-end gap-3 border-t border-line">
                <button
                  type="button"
                  onClick={() => setShowAddParentModal(false)}
                  className="px-4 py-2 rounded-xl border border-line text-ink hover:bg-canvas font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-primary text-white font-extrabold shadow-card hover:bg-primary-hover"
                >
                  Create Parent
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
